import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@auth/services/auth.service';
import { ProneService } from '@app/prone-operations/services/prone.service';
import { SessionContextService } from '@common/api/session-context.service';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

let isHandlingWebAuthError = false;
let isHandlingKioskSessionError = false;
let isHandlingKioskInfraError = false;

const isKioskRequest = (url: string): boolean => url.includes('/station/');
const isImpersonateRequest = (url: string): boolean => /\/auth\/(access-as|back)\//.test(url);

const NETWORK_CLIENT_ERRORS = [0, -1] as const;
const CONFIRMED_SERVER_ERRORS = [500, 502, 503, 504] as const;
// Pending validation with backend contract: same treatment for now, separated on purpose.
const PENDING_SERVER_ERRORS = [501, 505, 506, 507, 508, 510, 511] as const;

const isNetworkClientError = (status: number): boolean => NETWORK_CLIENT_ERRORS.includes(status as typeof NETWORK_CLIENT_ERRORS[number]);
const isConfirmedServerError = (status: number): boolean => CONFIRMED_SERVER_ERRORS.includes(status as typeof CONFIRMED_SERVER_ERRORS[number]);
const isPendingServerError = (status: number): boolean => PENDING_SERVER_ERRORS.includes(status as typeof PENDING_SERVER_ERRORS[number]);

interface ForcedLogoutDiagnostic {
  reason: string;
  status: number;
  url: string;
  method: string;
  response: unknown;
  isPublicFlow: boolean;
  currentPath: string;
  companyContextHeader: string | null;
  hasAuthorization: boolean;
  requestTokenId: string | null;
  storedUser: string | null;
  storedTokenId: string | null;
  timestamp: string;
}

function shouldLogForcedLogoutDiagnostic(): boolean {
  return !environment.production || localStorage.getItem('debugAuthErrors') === '1';
}

function readBearerTokenId(authorization: string | null): string | null {
  if (!authorization) return null;

  const match = authorization.match(/^Bearer\s+([^|]+)/i);
  return match?.[1] ?? null;
}

function buildForcedLogoutDiagnostic(
  req: Parameters<HttpInterceptorFn>[0],
  error: HttpErrorResponse,
  isPublicFlow: boolean,
): ForcedLogoutDiagnostic {
  const authorization = req.headers.get('Authorization');

  return {
    reason: 'web-session-like-error-forced-local-logout',
    status: error.status,
    url: req.urlWithParams,
    method: req.method,
    response: typeof error.error === 'string' ? error.error : error.message,
    isPublicFlow,
    currentPath: window.location.pathname,
    companyContextHeader: req.headers.get('x-company-context'),
    hasAuthorization: req.headers.has('Authorization'),
    requestTokenId: readBearerTokenId(authorization),
    storedUser: localStorage.getItem('user'),
    storedTokenId: localStorage.getItem('token')?.split('|')[0] ?? null,
    timestamp: new Date().toISOString(),
  };
}

function logForcedLogoutDiagnostic(
  req: Parameters<HttpInterceptorFn>[0],
  error: HttpErrorResponse,
  isPublicFlow: boolean,
): void {
  // TEMP DEBUG: remove after backend ownership/session errors are stabilized.
  if (!shouldLogForcedLogoutDiagnostic()) return;

  const diagnostic = buildForcedLogoutDiagnostic(req, error, isPublicFlow);
  console.error('[AuthInterceptor] Forced local logout after API auth/ownership error', diagnostic);
  sessionStorage.setItem('lastForcedLogoutDiagnostic', JSON.stringify(diagnostic));
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const proneService = inject(ProneService);
  const sessionContext = inject(SessionContextService);
  const stationRequest = isKioskRequest(req.url);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const status = error.status;
      const isPublicFlow = authService.isPublic();

      if (stationRequest) {
        const kioskSessionLikeError = [401, 403, 406, 408, 419].includes(status);
        if (kioskSessionLikeError) {
          if (!isHandlingKioskSessionError) {
            isHandlingKioskSessionError = true;

            // Read X-Logout headers from backend response
            const messageVariable = error.headers?.get('X-Logout-Message-Variable');
            const sense = error.headers?.get('X-Logout-Sense') ?? 'failure';
            const hora = error.headers?.get('X-Logout-Hora');

            if (messageVariable) {
              const SENSE_TO_COLOR: Record<string, string> = {
                success: '#06a106', failure: '#ef1111', info: '#808083',
                horaextra: '#d099d9', pausas: '#ffcb7f', permisos: '#b2c0c6',
              };
              const color = SENSE_TO_COLOR[sense] ?? '#ef1111';
              const dni = sessionContext.workerDni();
              const name = sessionContext.workerName();
              const maskedDni = dni ? dni.replace(/^(\w{2}).*(\w{2})$/, '$1*****$2') : null;

              // TODO: integrar modal prone asíncrono con ProneQuickDialogsFactoryService
              // Por ahora: log + goToStartupMode (estabilidad prioritaria)
              console.warn('[KioskLogout]', {
                messageVariable,
                sense,
                color,
                hora,
                dni: maskedDni,
                name,
              });
            }

            proneService.goToStartupMode();
            queueMicrotask(() => {
              isHandlingKioskSessionError = false;
            });
          }
          return throwError(() => error);
        }

        if (
          (isNetworkClientError(status) || isConfirmedServerError(status))
          && window.location.pathname.includes('/prone-operations')
        ) {
          if (!isHandlingKioskInfraError) {
            isHandlingKioskInfraError = true;
            void proneService.goSinConexionAndComeback().finally(() => {
              isHandlingKioskInfraError = false;
            });
          }
          return throwError(() => error);
        }

        if (isPendingServerError(status) && window.location.pathname.includes('/prone-operations')) {
          if (!isHandlingKioskInfraError) {
            isHandlingKioskInfraError = true;
            void proneService.goSinConexionAndComeback().finally(() => {
              isHandlingKioskInfraError = false;
            });
          }
          return throwError(() => error);
        }
      } else {
        const webSessionLikeError = [401, 403, 419].includes(status);
        if (webSessionLikeError && !isPublicFlow && !isImpersonateRequest(req.url)) {
          if (!isHandlingWebAuthError) {
            isHandlingWebAuthError = true;
            logForcedLogoutDiagnostic(req, error, isPublicFlow);
            authService.logout({ remote: false });
            queueMicrotask(() => {
              isHandlingWebAuthError = false;
            });
          }
          return throwError(() => error);
        }

        // 408 remains local in web flows (component/service level), no global action here.

        if (isNetworkClientError(status) || isConfirmedServerError(status)) {
          return throwError(() => error);
        }

        if (isPendingServerError(status)) {
          return throwError(() => error);
        }
      }

      return throwError(() => error);
    })
  );
};
